import React, { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback brain image
const FALLBACK_BRAIN = "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800&q=90";

const AnimatedBrain = ({ className = "" }) => {
  const [brainImage, setBrainImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrain = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/brain/generate`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.image_base64) {
            setBrainImage(`data:image/png;base64,${data.image_base64}`);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        console.error("Brain generation error:", e);
      }
      
      // Fallback
      setBrainImage(FALLBACK_BRAIN);
      setLoading(false);
    };
    
    fetchBrain();
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* Holographic brain container */}
      <div className="holographic-brain-scene relative w-full h-full flex items-center justify-center">
        
        {/* Outer glow ring */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="holographic-glow-ring w-[120%] h-[120%] rounded-full" />
        </div>

        {/* Main brain container */}
        <div className="brain-hologram-container relative">
          
          {loading ? (
            <div className="w-80 h-80 flex items-center justify-center">
              <div className="w-16 h-16 border-2 border-[#00D2FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="relative w-80 h-80">
              {/* The brain image with holographic effect */}
              <div className="brain-glow-wrapper">
                <img 
                  src={brainImage}
                  alt="Neural Intelligence"
                  className="brain-holographic-image w-full h-full object-contain"
                />
                
                {/* 3D volumetric lighting gradient overlay */}
                <div className="volumetric-3d-overlay" />
              </div>
              
              {/* Wireframe grid overlay */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 200 200">
                <defs>
                  <linearGradient id="gridGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#00D2FF" stopOpacity="0.7" />
                    <stop offset="33%" stopColor="#00B8D4" stopOpacity="0.8" />
                    <stop offset="66%" stopColor="#00BFA5" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#007FFF" stopOpacity="0.7" />
                  </linearGradient>
                  <filter id="gridGlow">
                    <feGaussianBlur stdDeviation="0.5" />
                  </filter>
                </defs>
                
                {/* Horizontal grid lines forming brain scan - BRIGHTER */}
                {[...Array(20)].map((_, i) => (
                  <line
                    key={`h-${i}`}
                    x1="40" 
                    y1={60 + i * 4}
                    x2="160" 
                    y2={60 + i * 4}
                    stroke="url(#gridGradient)"
                    strokeWidth="0.5"
                    opacity="0.7"
                    filter="url(#gridGlow)"
                    className="grid-line"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
                
                {/* Vertical grid lines - BRIGHTER */}
                {[...Array(15)].map((_, i) => (
                  <line
                    key={`v-${i}`}
                    x1={45 + i * 8}
                    y1="60"
                    x2={45 + i * 8}
                    y2="140"
                    stroke="url(#gridGradient)"
                    strokeWidth="0.5"
                    opacity="0.6"
                    filter="url(#gridGlow)"
                  />
                ))}
                
                {/* Central bright vertical line (corpus callosum) - MUCH BRIGHTER in NeuroMesh cyan */}
                <line
                  x1="100"
                  y1="60"
                  x2="100"
                  y2="140"
                  stroke="#00D2FF"
                  strokeWidth="2.5"
                  opacity="1"
                  filter="url(#gridGlow)"
                  className="central-line"
                />
                
                {/* Circular scan rings - blue and green */}
                <circle
                  cx="100"
                  cy="100"
                  r="35"
                  fill="none"
                  stroke="#00D2FF"
                  strokeWidth="0.5"
                  opacity="0.6"
                  className="scan-ring scan-ring-1"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="45"
                  fill="none"
                  stroke="#00BFA5"
                  strokeWidth="0.4"
                  opacity="0.5"
                  className="scan-ring scan-ring-2"
                />
              </svg>
              
              {/* Animated horizontal scan line */}
              <div className="scan-line-horizontal" />
              
              {/* Particle point cloud - MORE and BRIGHTER */}
              <div className="particle-cloud absolute inset-0 pointer-events-none">
                {[...Array(120)].map((_, i) => (
                  <div
                    key={i}
                    className="holographic-particle"
                    style={{
                      left: `${15 + Math.random() * 70}%`,
                      top: `${15 + Math.random() * 70}%`,
                      animationDelay: `${Math.random() * 3}s`,
                      animationDuration: `${2 + Math.random() * 2}s`
                    }}
                  />
                ))}
              </div>
              
              {/* Light traveling through brain */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 200 200">
                <defs>
                  <radialGradient id="travelLight">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
                    <stop offset="40%" stopColor="#00D2FF" stopOpacity="0.9" />
                    <stop offset="70%" stopColor="#00BFA5" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#007FFF" stopOpacity="0" />
                  </radialGradient>
                  <filter id="lightGlow">
                    <feGaussianBlur stdDeviation="3" />
                  </filter>
                </defs>
                
                {/* Traveling lights along neural pathways */}
                <circle r="4" fill="url(#travelLight)" filter="url(#lightGlow)">
                  <animateMotion dur="5s" repeatCount="indefinite" 
                    path="M70,90 Q80,80 90,85 Q100,88 110,90 Q120,92 125,98" />
                </circle>
                
                <circle r="3.5" fill="url(#travelLight)" filter="url(#lightGlow)">
                  <animateMotion dur="6s" repeatCount="indefinite" 
                    path="M125,98 Q120,108 110,112 Q100,115 90,110 Q82,106 78,100" />
                </circle>
                
                <circle r="3" fill="url(#travelLight)" filter="url(#lightGlow)">
                  <animateMotion dur="4s" repeatCount="indefinite" 
                    path="M100,70 Q102,80 100,90 Q98,100 100,110 Q102,120 100,130" />
                </circle>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Holographic styles */}
      <style>{`
        .holographic-brain-scene {
          filter: drop-shadow(0 0 40px rgba(0, 210, 255, 0.3));
          perspective: 1200px;
          transform-style: preserve-3d;
        }

        /* Brain glow wrapper for VERY strong 3D edge lighting with depth */
        .brain-glow-wrapper {
          filter: drop-shadow(0 5px 30px rgba(0, 255, 255, 1)) 
                  drop-shadow(0 10px 50px rgba(0, 255, 255, 0.8))
                  drop-shadow(0 15px 80px rgba(0, 210, 255, 0.6))
                  drop-shadow(0 20px 120px rgba(0, 210, 255, 0.4))
                  drop-shadow(0 -5px 30px rgba(0, 255, 255, 0.6));
          transform: translateZ(50px);
        }

        /* Outer glow ring */
        .holographic-glow-ring {
          background: radial-gradient(circle, rgba(0, 210, 255, 0.15) 0%, transparent 70%);
          animation: glowPulse 3s ease-in-out infinite;
        }
        @keyframes glowPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.05); opacity: 1; }
        }

        /* Brain image - ONLY BLUE AND GREEN SHADES */
        .brain-holographic-image {
          filter: brightness(0.6) contrast(1.7) saturate(2.5) hue-rotate(170deg) invert(0.08);
          mix-blend-mode: screen;
          opacity: 0.55;
          animation: brainPulse3D 4s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        @keyframes brainPulse3D {
          0%, 100% { 
            transform: scale(1) rotateX(0deg) rotateZ(0deg) translateZ(0);
            filter: brightness(0.6) contrast(1.7) saturate(2.5) hue-rotate(170deg) invert(0.08);
            opacity: 0.55;
          }
          50% { 
            transform: scale(1.02) rotateX(2deg) rotateZ(1deg) translateZ(5px);
            filter: brightness(0.7) contrast(1.8) saturate(2.6) hue-rotate(175deg) invert(0.08);
            opacity: 0.7;
          }
        }

        /* Grid lines pulse */
        .grid-line {
          animation: gridPulse 3s ease-in-out infinite;
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }

        /* Central line glow */
        .central-line {
          animation: centralGlow 2s ease-in-out infinite;
        }
        @keyframes centralGlow {
          0%, 100% { opacity: 0.6; stroke-width: 1.5; }
          50% { opacity: 1; stroke-width: 2; }
        }

        /* Scan rings expand */
        .scan-ring {
          animation: scanExpand 4s ease-out infinite;
          transform-origin: center;
        }
        .scan-ring-1 { animation-delay: 0s; }
        .scan-ring-2 { animation-delay: 2s; }
        
        @keyframes scanExpand {
          0% { r: 30; opacity: 0; }
          20% { opacity: 0.6; }
          100% { r: 60; opacity: 0; }
        }

        /* Horizontal scan line - blue to green gradient */
        .scan-line-horizontal {
          position: absolute;
          left: 10%;
          right: 10%;
          height: 2px;
          background: linear-gradient(to right, 
            transparent 0%, 
            rgba(0, 210, 255, 0.4) 20%, 
            rgba(0, 191, 165, 0.7) 40%,
            rgba(0, 127, 255, 0.9) 60%, 
            rgba(0, 210, 255, 0.4) 80%, 
            transparent 100%
          );
          box-shadow: 0 0 10px rgba(0, 210, 255, 0.8), 0 0 20px rgba(0, 191, 165, 0.5);
          animation: scanMove 4s linear infinite;
        }
        @keyframes scanMove {
          0% { top: 20%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 80%; opacity: 0; }
        }

        /* Holographic particles - blue and green shades */
        .holographic-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #00D2FF;
          border-radius: 50%;
          box-shadow: 0 0 6px #00D2FF, 0 0 12px #00BFA5, 0 0 18px rgba(0, 210, 255, 0.5);
          animation: particleTwinkle 3s ease-in-out infinite;
        }
        @keyframes particleTwinkle {
          0%, 100% { 
            opacity: 0.5; 
            transform: scale(0.8); 
            background: #00D2FF;
          }
          33% {
            opacity: 0.8;
            transform: scale(1.2);
            background: #00BFA5;
          }
          66% { 
            opacity: 1; 
            transform: scale(1.5); 
            background: #007FFF;
          }
        }

        /* Container with subtle float - flat top-down view */
        .brain-hologram-container {
          animation: containerFloat3D 6s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        @keyframes containerFloat3D {
          0%, 100% { transform: translateY(0) rotateZ(0deg); }
          50% { transform: translateY(-10px) rotateZ(2deg); }
        }

        /* 3D volumetric lighting overlay - NeuroMesh colors with transparency gradient */
        .volumetric-3d-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 30%, 
            rgba(0, 210, 255, 0.2) 0%, 
            rgba(0, 127, 255, 0.1) 30%,
            transparent 50%),
            radial-gradient(ellipse at 50% 70%, 
            rgba(0, 210, 255, 0.15) 0%, 
            transparent 50%),
            linear-gradient(to bottom,
            rgba(0, 210, 255, 0.05) 0%,
            transparent 40%,
            rgba(0, 127, 255, 0.08) 60%,
            transparent 100%);
          mix-blend-mode: screen;
          pointer-events: none;
          animation: volumetricShift 5s ease-in-out infinite;
        }
        @keyframes volumetricShift {
          0%, 100% { opacity: 0.7; transform: translateY(0); }
          50% { opacity: 0.95; transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
};

export default AnimatedBrain;
